import { createException, RegistryErrorCode } from '../errors/RegistryError';
import { IRegistryDataTransport, IResourceLoader } from '../registry.types';
import { isBrowser } from '../flags';
import { evaluateBundle } from './evaluateBundle';
import { getScriptTag, getGlobalFactoryModel } from './getModel';
import { createRegistryDataTransport } from './createRegistryDataTransport';
import { createEnvironmentLoader } from './createEnvironmentLoader';
import { loadAssets } from './loadAssets';

const HOT_BUNDLE_ID = 'hot';

async function fetchAndEvaluate<TResponse = any>({
  fetcher,
  url,
  id,
  title = 'Bundle',
  globals,
}: {
  fetcher: (url: string) => Promise<string>;
  url: string;
  id?: string;
  title?: string;
  globals?: Record<string, any>;
}): Promise<TResponse> {
  let bundle: string;

  try {
    bundle = await fetcher(url);
  } catch (e) {
    throw createException(`${title} fetch error`, RegistryErrorCode.FetchError)
      .withOriginalErrorMessage(e.message)
      .withUrl(url);
  }

  try {
    return evaluateBundle(bundle, { id, globals });
  } catch (e) {
    throw createException(
      `${title} evaluation error`,
      RegistryErrorCode.BundleEvaluationError,
    )
      .withOriginalErrorMessage(e.message)
      .withUrl(url);
  }
}

function loadScript<TResponse = any | void>({
  id,
  url,
  title = 'Bundle',
}: {
  id?: string;
  url: string;
  title: string;
}): Promise<TResponse> {
  try {
    const script = document.createElement('script');
    script.src = url;

    document.head.appendChild(script);
    return new Promise<TResponse>(resolve => {
      script.addEventListener('load', async () => {
        if (id) {
          const model = getGlobalFactoryModel(id);
          resolve(model);
        } else {
          resolve(undefined as any);
        }
      });
    });
  } catch (e) {
    throw createException(
      `${title} loading error`,
      RegistryErrorCode.BundleEvaluationError,
    )
      .withOriginalErrorMessage(e.message)
      .withUrl(url);
  }
}

function createPromiseGuard<T = any>() {
  const requests: Map<string, Promise<T>> = new Map();

  return async (id: string, factory: () => Promise<T>) => {
    if (requests.has(id)) {
      return requests.get(id) as Promise<T>;
    }

    const pending = factory();

    requests.set(id, pending);

    try {
      const model = await pending;
      return model;
    } catch (e) {
      requests.delete(id);
      throw e;
    }
  };
}

function withRetry<TResponse = any>({
  times,
  task,
}: {
  task: () => Promise<TResponse>;
  times: number;
}) {
  let counter = 0;
  return new Promise<TResponse>((resolve, reject) => {
    const run = async () => {
      try {
        const response = await task();
        resolve(response);
      } catch (e) {
        counter++;
        if (counter >= times) {
          reject(e);
        } else {
          run();
        }
      }
    };

    run();
  });
}

export function createResourceLoader(params: {
  useScriptsInsteadOfEval: boolean;
  transport?: Partial<IRegistryDataTransport>;
  shouldLoadCSSAssets?: boolean;
  globals?: Record<string, any>;
}): IResourceLoader {
  const api = {
    ...createRegistryDataTransport(),
    ...params.transport,
  };

  const loadResource = <TResponse = any>({
    fetcher,
    url,
    id,
    title = 'Bundle',
  }: {
    fetcher: (url: string) => Promise<string>;
    url: string;
    id?: string;
    title?: string;
  }): Promise<TResponse> => {
    return withRetry<TResponse>({
      times: 3,
      task: () => {
        if (params.useScriptsInsteadOfEval && isBrowser) {
          return loadScript({
            id,
            url,
            title,
          });
        } else {
          return fetchAndEvaluate({
            fetcher,
            url,
            id,
            title,
            globals: params.globals,
          });
        }
      },
    });
  };

  return {
    async fetchLibraryManifest({ url }) {
      return api.fetchLibraryManifest(url);
    },
    async fetchLibraryRuntime({ url }) {
      const element = getScriptTag(url);

      if (element) {
        return Promise.resolve();
      }

      return loadResource({
        url,
        title: 'Library Runtime Bundle',
        fetcher: api.fetchLibraryRuntime,
      });
    },
    async fetchLibraryHotBundle({ url }) {
      const element = getScriptTag(url);

      if (element) {
        return Promise.resolve();
      }

      return loadResource({
        url,
        id: HOT_BUNDLE_ID,
        title: 'Library Hot Bundle',
        fetcher: api.fetchLibraryHotBundle,
      });
    },

    createBundleLoader({ environment }) {
      const environmentLoader = environment
        ? createEnvironmentLoader({
            loadHotBundle: this.fetchLibraryHotBundle,
            loadRuntime: this.fetchLibraryRuntime,
            environment,
          })
        : null;

      const guard = createPromiseGuard();

      return async ({ url, id, assets = [] }) => {
        /**
         * `loadingAssetsPromise` – not sure we should wait until CSS is loaded,
         * since it may cause waterfall
         */
        if (assets && params.shouldLoadCSSAssets) {
          /**
           * NOTE: loadingAssetsPromise = loadAssets(assets); ?
           */
          loadAssets(assets);
        }

        if (id) {
          const model = await getGlobalFactoryModel(id);
          if (model) {
            return model;
          }
        }

        const loadingBundlePromise = url
          ? guard(url, async () => {
              if (environmentLoader && !environmentLoader.isLoaded()) {
                await environmentLoader.load();
              }

              return loadResource({
                url,
                id,
                title: `Bundle "${url}"`,
                fetcher: api.fetchComponentsBundle,
              });
            })
          : null;

        await loadingBundlePromise;

        if (id) {
          return getGlobalFactoryModel(id);
        } else {
          return null;
        }
      };
    },
  };
}
